import { T as Methods, n as SOFT_HYPHEN$1, r as SOFT_HYPHEN_MARKER, t as SlugSchema } from "./slug.schemas--_8HzbpI.js";
import * as z$1 from "zod";

//#region src/errors/auto-cast.ts
globalThis.__sparkErrorAutoCastRegistry ??= /* @__PURE__ */ new Map();
const registry = globalThis.__sparkErrorAutoCastRegistry;
/**
* @param ctor Error constructor.
* @param cast Cast to convert error.
* @example
* registerErrorAutoCast(ZodError, (err) => ValidationError.fromZod(err));
*/
function registerErrorAutoCast(ctor, cast) {
	const existing = registry.get(ctor.name) ?? [];
	registry.set(ctor.name, [...existing, {
		ctor,
		cast
	}]);
}
/**
* @param error The error to potentially auto-cast.
* @returns If the error matches the registered error constructor, it will be converted to SparkError using the registered cast function. Otherwise, the original error will be returned.
*/
function autoCastError(error) {
	let proto = Object.getPrototypeOf(error);
	while (proto !== null) {
		const ctor = proto.constructor;
		const cast = registry.get(ctor.name);
		if (cast && cast.length > 0) {
			for (const { ctor: registeredCtor, cast: registeredCast } of cast) if (error instanceof registeredCtor) return registeredCast(error);
			return cast[0].cast(error);
		}
		proto = Object.getPrototypeOf(proto);
	}
	return error;
}

//#endregion
//#region src/errors/base.ts
const ERROR_TAG = Symbol.for("team-blink.errors.spark-error");
/**
* Abstract base error class for all Spark errors.
* This class provides a common interface for all Spark errors, including:
*
*  - error id, unique per error instance
*  - error code, unique per type of error
*  - error context
* @template Context - The required context for the error. Defaults to an empty object.
*/
var BaseSparkError = class extends Error {
	/**
	* @ignore
	* @internal
	* @hidden
	*/
	static [ERROR_TAG] = true;
	/**
	* Overrides for the error instance.
	* @example {"message": "Custom error message"}
	* @example {"statusCode": 419}
	*/
	overrides;
	/**
	* Error id, unique per error instance.
	* This is a UUIDv4 string that is generated when the error is created.
	* @example '123e4567-e89b-12d3-a456-426614174000'
	*/
	id = crypto.randomUUID();
	/**
	* Unique error code.
	* @example 'fetch-error'
	* @example 'not-found-error'
	*/
	static code;
	/**
	* @see {@link BaseSparkError.code}
	*/
	get code() {
		return this.overrides?.code ?? this.constructor.code;
	}
	/**
	* Error code HTTP status.
	* @example 404
	* @example 500
	*/
	static statusCode = 500;
	/**
	* @see {@link BaseSparkError.statusCode}
	*/
	get statusCode() {
		return this.overrides?.statusCode ?? this.constructor.statusCode;
	}
	static expected = false;
	get expected() {
		return this.overrides?.expected ?? this.constructor.expected;
	}
	/**
	* Contextual information about the error.
	*/
	context;
	constructor(...args) {
		super();
		const { context, overrides } = splitContextAndMessage(args);
		this.overrides = overrides;
		this.context = context;
		this.message = overrides.message ?? this.defaultMessage();
		if ("cause" in overrides) this.cause = overrides.cause;
	}
};
function isSparkError(error, sparkErrorType) {
	if (!isBaseSparkError(error)) return false;
	if (!sparkErrorType) return true;
	return error instanceof sparkErrorType || error.code === sparkErrorType.code;
}
function isBaseSparkError(error) {
	if (!error) return false;
	if (error instanceof BaseSparkError) return true;
	if (typeof error === "object" && ERROR_TAG in error && error[ERROR_TAG] === true) {
		console.warn("Warning: An object with the Spark error tag was detected, but it is not an instance of BaseSparkError. This may indicate that the error was created with a different version of a @team-blink/* package. Make sure @team-blink/* packages are always peerDependencies.", error);
		return true;
	}
	return false;
}
/** @ignore */
function splitContextAndMessage(args) {
	if (args.length === 0) return {
		context: {},
		overrides: {}
	};
	else if (args.length === 2) return {
		context: args[0],
		overrides: args[1] ?? {}
	};
	const contextOrOptions = args[0] ?? {};
	const keys = Object.keys(contextOrOptions);
	if (keys.length === 0) return {
		context: {},
		overrides: {}
	};
	else if (keys.reduce((acc, key) => acc && [
		"message",
		"statusCode",
		"code",
		"cause",
		"expected"
	].includes(key), true)) return {
		context: {},
		overrides: contextOrOptions
	};
	return {
		context: contextOrOptions,
		overrides: {}
	};
}

//#endregion
//#region src/errors/expected.ts
const EXPECTED_TAG = Symbol.for("team-blink.errors.expected");
function markExpected(error, expected = true) {
	if (error && typeof error === "object") Object.defineProperty(error, EXPECTED_TAG, {
		value: expected,
		configurable: true,
		enumerable: false,
		writable: true
	});
	return error;
}
function isExpectedError(error) {
	if (!error || typeof error !== "object") return false;
	const marked = error[EXPECTED_TAG];
	if (typeof marked === "boolean") return marked;
	return isSparkError(error) && error.expected;
}

//#endregion
//#region src/errors/validation.ts
/**
* ValidationError is thrown when data parsing or validation fails.
*/
var ValidationError$1 = class ValidationError$1 extends BaseSparkError {
	static code = "validation-error";
	static statusCode = 400;
	defaultMessage() {
		const errors = this.context?.errors.map(printErrorDetails);
		if (errors.length > 0) return `Data validation failed: ${errors.join("; ")}`;
		return "Data validation failed.";
	}
	/**
	* Create a validation error from a single detail object.
	* Path is optional and defaults to ".".
	* @param details - Details about the validation error.
	* @returns A new ValidationError instance.
	*/
	static fromDetails(details) {
		return new ValidationError$1({ errors: [{
			...details,
			path: details.path ?? "."
		}] });
	}
	static joinPath(base, addition) {
		if (!base || base === "." && addition !== ".") return `.${addition}`;
		if (addition === "." || !addition) return base ?? ".";
		if (addition.startsWith("[")) return `${base}${addition}`;
		return `${base}.${addition}`;
	}
};
/** @ignore */
function printErrorDetails(details) {
	let msg = `At path '${details.path}': expected ${details.expected}`;
	if (details.value !== void 0) msg += `, got ${JSON.stringify(details.value)}`;
	if (details.reason) msg += ` (${details.reason})`;
	return msg;
}

//#endregion
//#region src/json.ts
/**
* !Note: This function does not perform any runtime checks.
* It is a type cast that tells TypeScript to treat the value as JSON-serializable.
* Use with caution, as it can lead to runtime errors if the value is not actually JSON-serializable.
* @param value - The value to cast to JSON-serializable type.
* @returns The value, typed as JSON-serializable.
*/
function castJson(value) {
	return value;
}

//#endregion
//#region src/models/api-key.ts
/**
* @param apiKey API key to turn into a token.
* @returns The token a client sends to authenticate with this key.
*/
function apiKeyToToken(apiKey) {
	return `${apiKey.keyPrefix}_${apiKey.keyId}${apiKey.keySecret}`;
}

//#endregion
//#region src/models/method.ts
/**
* Converts a Spark method to its corresponding slug.
*
* @see {@link slugToMethod} for the reverse conversion.
*
* @param method The Spark method.
* @returns The corresponding Spark method slug.
*/
function methodToSlug(method) {
	if (method === "bwgeintegreerd") return "geintegreerd";
	return method;
}
/**
* Converts a Spark method slug to its corresponding method.
*
* @see {@link methodToSlug} for the reverse conversion.
*
* @param slug The Spark method slug.
* @returns The corresponding Spark method.
*/
function slugToMethod(slug) {
	if (slug === "geintegreerd") return "bwgeintegreerd";
	return slug;
}

//#endregion
//#region src/models/authorization.ts
const WILDCARD = "*";
/**
* Check for at least one matching item.
* @see https://en.wikipedia.org/wiki/Existential_quantification
*/
const HAS_ANY = "∃";
/**
* Check whether all items match.
* @see https://en.wikipedia.org/wiki/Universal_quantification
*/
const HAS_ALL = "∀";
/**
* Resources available per domain.
*/
const RESOURCES = {
	method: Methods,
	beheer: []
};
/**
* @param domain Domain to get resources for.
* @returns list of resources.
*/
function getResourcesForDomain(domain) {
	return RESOURCES[domain];
}
/**
* Actions available per domain.
*/
const ACTIONS = {
	method: ["edit", "publish"],
	beheer: ["edit", "read"]
};
/**
* Actions available for a domain.
* @param domain Domain to get actions for.
* @returns list of actions.
*/
function getActionsForDomain(domain) {
	return ACTIONS[domain];
}
/**
* Normalize the three check forms into a {@link Check}.
* @param domainOrScopeOrCheck Domain, scope, access check, or already normalized check.
* @param resourceOrAction Resource (domain form) or action (scope form).
* @param action Action (domain form).
* @returns Normalized check.
*/
function toCheck(domainOrScopeOrCheck, resourceOrAction, action) {
	if (typeof domainOrScopeOrCheck === "object") return domainOrScopeOrCheck;
	if (typeof action !== "undefined") return {
		kind: "domain",
		domain: domainOrScopeOrCheck,
		resource: resourceOrAction,
		action
	};
	if (typeof resourceOrAction !== "undefined") return {
		kind: "scope",
		scope: domainOrScopeOrCheck,
		action: resourceOrAction
	};
	return {
		kind: "check",
		check: domainOrScopeOrCheck
	};
}

//#endregion
//#region src/models/cms/cms.ts
/**
* @param item Item to check CMS state for.
* @returns True if published, might be pending retraction, but not yet retracted.
*/
function isPublished(item) {
	return item.publicationTag === null;
}
/**
* @param item Item to check CMS state for.
* @returns True if pending retraction, but still published (not yet retracted).
*/
function isPendingRetraction(item) {
	return item.retractionTag !== null;
}
/**
* @param item Item to check CMS state for.
* @returns True if draft, not yet published.
*/
function isDraft(item) {
	return !!item.publicationTag && !item.publicationTag.endsWith("_depublished");
}
/**
* @param item Item to check CMS state for.
* @returns True if retracted, meaning it was published but is now retracted.
*/
function isRetracted(item) {
	return !!item.publicationTag && item.publicationTag.endsWith("_depublished");
}

//#endregion
//#region src/models/internal-role.ts
/**
* Class representing internal roles and permissions.
*/
var InternalRoles = class InternalRoles {
	permissions = /* @__PURE__ */ new Map();
	constructor() {}
	/**
	* @returns No permissions or access given.
	*/
	static noAccess() {
		return new InternalRoles();
	}
	/**
	* @param roles Parse from list of roles.
	* @returns InternalRoles set
	*/
	static from(roles) {
		const result = new InternalRoles();
		if (roles) for (const role of roles) result.allow(role);
		return result;
	}
	/**
	* @returns JSON representation of the internal roles.
	*/
	toJSON() {
		const result = [];
		for (const [domain, domainMap] of this.permissions) {
			const resources = getResourcesForDomain(domain);
			const actions = getActionsForDomain(domain);
			const resourcesToCheck = resources.length > 0 ? resources : ["*"];
			if (resourcesToCheck.every((r) => actions.every((a) => domainMap.get(r)?.has(a)))) {
				result.push(`Spark.${domain}/*/*`);
				continue;
			}
			const addedAsWildcard = /* @__PURE__ */ new Set();
			const resourcesWithAllActions = resourcesToCheck.filter((r) => actions.every((a) => domainMap.get(r)?.has(a)));
			if (resourcesWithAllActions.length >= 2) for (const resource of resourcesWithAllActions) {
				result.push(`Spark.${domain}/${resource}/*`);
				for (const action of actions) addedAsWildcard.add(`${resource}/${action}`);
			}
			else {
				for (const action of actions) if (resourcesToCheck.every((r) => domainMap.get(r)?.has(action))) {
					result.push(`Spark.${domain}/*/${action}`);
					for (const resource of resourcesToCheck) addedAsWildcard.add(`${resource}/${action}`);
				}
				if (!actions.some((action) => resourcesToCheck.every((r) => domainMap.get(r)?.has(action)))) for (const resource of resourcesWithAllActions) {
					result.push(`Spark.${domain}/${resource}/*`);
					for (const action of actions) addedAsWildcard.add(`${resource}/${action}`);
				}
			}
			for (const [resource, actionSet] of domainMap) for (const action of actionSet) if (!addedAsWildcard.has(`${resource}/${action}`)) result.push(`Spark.${domain}/${resource}/${action}`);
		}
		return result;
	}
	/**
	* @returns Iterator over internal roles.
	*/
	*[Symbol.iterator]() {
		yield* this.toJSON();
	}
	/**
	* @param role Access or permission to forbid.
	*/
	forbid(role) {
		const [domainPart, resourcePart, actionPart] = role.split("/");
		const domain = domainPart.replace("Spark.", "");
		const domainMap = this.permissions.get(domain);
		if (!domainMap) return;
		if (resourcePart === "*") {
			const resources = getResourcesForDomain(domain);
			if (actionPart === "*") {
				if (resources.length === 0) domainMap.delete("*");
				else for (const resource of resources) domainMap.delete(resource);
			} else if (resources.length === 0) {
				const resourceSet = domainMap.get("*");
				if (resourceSet) {
					resourceSet.delete(actionPart);
					if (resourceSet.size === 0) domainMap.delete("*");
				}
			} else for (const resource of resources) {
				const resourceSet = domainMap.get(resource);
				if (resourceSet) {
					resourceSet.delete(actionPart);
					if (resourceSet.size === 0) domainMap.delete(resource);
				}
			}
		} else {
			const resourceSet = domainMap.get(resourcePart);
			if (!resourceSet) return;
			if (actionPart === "*") domainMap.delete(resourcePart);
			else {
				resourceSet.delete(actionPart);
				if (resourceSet.size === 0) domainMap.delete(resourcePart);
			}
		}
		if (domainMap.size === 0) this.permissions.delete(domain);
	}
	/**
	* @param role Access or permission to allow.
	*/
	allow(role) {
		const [domainPart, resourcePart, actionPart] = role.split("/");
		const domain = domainPart.replace("Spark.", "");
		if (!this.permissions.has(domain)) this.permissions.set(domain, /* @__PURE__ */ new Map());
		const domainMap = this.permissions.get(domain);
		if (resourcePart === "*") {
			const resources = getResourcesForDomain(domain);
			const actions = getActionsForDomain(domain);
			if (actionPart === "*") {
				if (resources.length === 0) {
					if (!domainMap.has("*")) domainMap.set("*", /* @__PURE__ */ new Set());
					for (const action of actions) domainMap.get("*").add(action);
				} else for (const resource of resources) {
					if (!domainMap.has(resource)) domainMap.set(resource, /* @__PURE__ */ new Set());
					for (const action of actions) domainMap.get(resource).add(action);
				}
			} else if (resources.length === 0) {
				if (!domainMap.has("*")) domainMap.set("*", /* @__PURE__ */ new Set());
				domainMap.get("*").add(actionPart);
			} else for (const resource of resources) {
				if (!domainMap.has(resource)) domainMap.set(resource, /* @__PURE__ */ new Set());
				domainMap.get(resource).add(actionPart);
			}
		} else {
			if (!domainMap.has(resourcePart)) domainMap.set(resourcePart, /* @__PURE__ */ new Set());
			const resourceSet = domainMap.get(resourcePart);
			if (actionPart === "*") {
				const actions = getActionsForDomain(domain);
				for (const action of actions) resourceSet.add(action);
			} else resourceSet.add(actionPart);
		}
	}
	can(domainOrScopeOrCheck, resourceOrAction, action) {
		const check = toCheck(domainOrScopeOrCheck, resourceOrAction, action);
		let domain;
		let resource;
		let actionToCheck;
		switch (check.kind) {
			case "domain":
				domain = check.domain;
				resource = check.resource;
				actionToCheck = check.action;
				break;
			case "scope": {
				const parts = check.scope.split("/");
				domain = parts[0].replace("Spark.", "");
				resource = parts[1];
				actionToCheck = check.action;
				break;
			}
			case "check": {
				const parts = check.check.split("/");
				domain = parts[0].replace("Spark.", "");
				resource = parts[1];
				actionToCheck = parts[2];
				break;
			}
		}
		const domainMap = this.permissions.get(domain);
		if (!domainMap) return false;
		const resources = getResourcesForDomain(domain);
		const actions = getActionsForDomain(domain);
		const resourcesToCheck = resources.length > 0 ? resources : ["*"];
		if (resource === "∀") {
			if (actionToCheck === "∀") return resourcesToCheck.every((r) => actions.every((a) => domainMap.get(r)?.has(a)));
			else if (actionToCheck === "∃") return resourcesToCheck.every((r) => actions.some((a) => domainMap.get(r)?.has(a)));
			else return resourcesToCheck.every((r) => domainMap.get(r)?.has(actionToCheck));
		} else if (resource === "∃") {
			if (actionToCheck === "∀") return resourcesToCheck.some((r) => actions.every((a) => domainMap.get(r)?.has(a)));
			else if (actionToCheck === "∃") return resourcesToCheck.some((r) => actions.some((a) => domainMap.get(r)?.has(a)));
			else return resourcesToCheck.some((r) => domainMap.get(r)?.has(actionToCheck));
		} else {
			const resourceSet = domainMap.get(resource);
			if (!resourceSet) return false;
			if (actionToCheck === "∀") return actions.every((a) => resourceSet.has(a));
			else if (actionToCheck === "∃") return actions.some((a) => resourceSet.has(a));
			else return resourceSet.has(actionToCheck);
		}
	}
};

//#endregion
//#region src/models/title.ts
const SOFT_HYPHEN = /\u00ad/g;
const VISIBLE_DASHES = /[\u2010-\u2015\u2212]/g;
function normalizeForSearch(text) {
	return text.replace(SOFT_HYPHEN, "").replace(VISIBLE_DASHES, "-").trim().toLowerCase();
}

//#endregion
//#region src/result.ts
function ok(value) {
	return {
		ok: value,
		unwrap() {
			return value;
		},
		unwrapOr(_defaultValue) {
			return value;
		},
		unwrapOrElse(_fn) {
			return value;
		},
		unwrapErr() {
			throw new Error("called `unwrapErr()` on an `Ok` value");
		},
		expect(_message) {
			return value;
		},
		and(fn) {
			return fn(value);
		},
		orElse(_fn) {
			return ok(value);
		},
		map(fn) {
			return ok(fn(value));
		},
		mapErr(_fn) {
			return ok(value);
		},
		isErr() {
			return false;
		},
		isOk() {
			return true;
		}
	};
}
/**
* Creates an `Err` variant of {@link Result} containing `error`.
*
* ```ts
* const result: Result<number> = err(new Error("failed"));
* ```
*/
function err(error) {
	return {
		error,
		unwrap() {
			throw error;
		},
		unwrapOr(defaultValue) {
			return defaultValue;
		},
		unwrapOrElse(fn) {
			return fn(error);
		},
		unwrapErr() {
			return error;
		},
		expect(message) {
			throw new Error(message, { cause: error });
		},
		and(_fn) {
			return err(error);
		},
		orElse(fn) {
			return fn(error);
		},
		map(_fn) {
			return err(error);
		},
		mapErr(fn) {
			return err(fn(error));
		},
		isErr() {
			return true;
		},
		isOk() {
			return false;
		}
	};
}

//#endregion
//#region src/parsing/error/sources/manual.ts
var ManualIssue = class {
	data;
	constructor(data) {
		this.data = data;
	}
	get source() {
		return {
			kind: "manual",
			issue: this.data
		};
	}
	get path() {
		return this.data.path;
	}
	get expected() {
		return this.data.expected;
	}
	get value() {
		return this.data;
	}
	get message() {
		return this.data.message;
	}
	toJSON() {
		return this.data;
	}
};

//#endregion
//#region src/parsing/error/sources/zod-issue.ts
var ZodIssue = class ZodIssue {
	issue;
	data;
	constructor(issue, data) {
		this.issue = issue;
		this.data = data;
	}
	static fromZodError(error, data) {
		return error.issues.map((issue) => ZodIssue.fromZodIssue(issue, data));
	}
	static fromZodIssue(issue, data) {
		return new ZodIssue(issue, fetchData(data, issue.path));
	}
	get source() {
		return {
			kind: "zod",
			issue: this.issue
		};
	}
	get path() {
		return this.issue.path;
	}
	get expected() {
		return this.issue.message;
	}
	get value() {
		return this.data;
	}
	get message() {
		return this.issue.message;
	}
	toJSON() {
		return {
			path: this.path,
			expected: this.expected,
			value: this.value,
			message: this.message
		};
	}
};
function fetchData(data, path) {
	return path.reduce((current, segment) => {
		if (typeof current === "object" && current !== null) return current[segment];
	}, data);
}

//#endregion
//#region src/parsing/error/validation-error.ts
/**
* Validation error. Contains a message describing the error.
* @augments ValidationErrorItem
*/
var ValidationError = class ValidationError extends BaseSparkError {
	static code = "validation-error";
	static statusCode = 400;
	defaultMessage() {
		if (this.issues.length > 0) return `Validation failed: ${this.issues.map((issue) => {
			const path = issue.path.join(".");
			return path ? `${path}: ${issue.message}` : issue.message;
		}).join("; ")}`;
		return "Validation failed.";
	}
	get issues() {
		return this.context.issues;
	}
	/**
	* An error for when validation fails.
	* @param issues - The list of validation issues that occurred.
	*/
	constructor(issues) {
		super({ issues });
		this.name = ValidationError.name;
	}
	get isEmpty() {
		return this.issues.length <= 0;
	}
	issuesForPath(path, matching) {
		const isFullMatch = matching === "full";
		return this.issues.filter((issue) => {
			if (isFullMatch && issue.path.length !== path.length) return false;
			return path.every((segment, index) => segment === issue.path[index]);
		});
	}
	addError(error) {
		this.context.issues.push(error);
		return this;
	}
	addErrors(additional, path = []) {
		const source = Array.isArray(additional) ? additional : [additional];
		const toAdd = [];
		for (const item of source) if (isSparkError(item, ValidationError)) toAdd.push(...item.issues);
		else if (item instanceof ZodIssue) toAdd.push(item);
		else if ("path" in item && "message" in item) toAdd.push(item);
		else if (item instanceof z$1.ZodError) toAdd.push(...ZodIssue.fromZodError(item));
		else throw new Error("Invalid error type provided to addErrors");
		if (path.length === 0) {
			this.context.issues.push(...toAdd);
			return this;
		}
		return this;
	}
	static empty() {
		return new ValidationError([]);
	}
	static new(path, message, expected, value) {
		return new ValidationError([new ManualIssue({
			path,
			message,
			expected,
			value
		})]);
	}
	static fromZod(error, data) {
		return new ValidationError(ZodIssue.fromZodError(error, data));
	}
};
registerErrorAutoCast(z$1.ZodError, ValidationError.fromZod);

//#endregion
//#region src/parsing/parse.ts
/**
*
* @param data
* @param schema
* @param options
*/
function parse(data, schema) {
	const zodResult = schema.safeParse(data);
	return zodResult.success ? ok(zodResult.data) : err(ValidationError.fromZod(zodResult.error, data));
}

//#endregion
//#region src/parsing/validate.ts
/**
* Validates data against a Schema. Throws an error when the validation failed.
* @deprecated Use `parse` instead. This function is just an alias for `parse` and will be removed in a future release.
* @param data
* @param schema
* @throws {ValidationError} Data must be valid or an error is thrown.
*/
function validate(data, schema) {
	return parse(data, schema).unwrap();
}

//#endregion
//#region src/primitives/domain.ts
/**
* @param domain Domain name or URL.
* @returns Parent domain or the same domain if no parent exists.
* @example `parentDomain("sub.example.com") // "example.com"`
* @example `parentDomain("example.com") // "example.com"`
* @example `parentDomain("localhost") // "localhost"`
*/
function parentDomain(domain) {
	const hostname = typeof domain === "string" ? domain : domain.hostname;
	const parts = hostname.split(".");
	return parts.length <= 2 ? hostname : parts.slice(1).join(".");
}
/**
* @param url URL to convert to wildcard domain.
* @returns URL with wildcard subdomain.
* @example `wildcardDomain(new URL("https://sub.example.com/path")) // new URL("https://*.example.com/path")`
* @example `wildcardDomain(new URL("https://sub.sub.example.com/path")) // new URL("https://*.sub.example.com/path")`
* @example `wildcardDomain(new URL("https://example.com/path")) // new URL("https://example.com/path")`
* @example `wildcardDomain(new URL("https://localhost/path")) // new URL("https://localhost/path")`
*/
function wildcardDomain(url) {
	const parsedUrl = new URL(url);
	const parts = parsedUrl.hostname.split(".");
	if (parts.length <= 2) return parsedUrl;
	parts[0] = "*";
	parsedUrl.hostname = parts.join(".");
	return parsedUrl;
}

//#endregion
//#region src/primitives/slug.ts
const HYPHENATION_MARKERS = new RegExp(`[${"~"}${"­"}]`, "g");
/**
* @param text The text to slugify.
* @returns Slugified string, or an empty string when nothing slugifiable remains.
*/
function slugifyLoose(text) {
	if (typeof text !== "string") return "";
	return text.replaceAll(HYPHENATION_MARKERS, "").toLowerCase().trim().normalize("NFD").replaceAll(/[\u0300-\u036F]/g, "").replaceAll(/\s+/g, "-").replaceAll(/[^\da-z-]/g, "-").replaceAll(/-{2,}/g, "-").replaceAll(/^-|-$/g, "");
}
/**
* @param text The text to slugify.
* @returns Slugified string.
* @throws If the text cannot be slugified to a valid Slug.
*/
function slugify(text) {
	return SlugSchema.parse(slugifyLoose(text));
}

//#endregion
//#region src/telemetry.ts
function telemetryServiceName(packageName) {
	return packageName.replace(/^@/, "").replaceAll("/", ".");
}

//#endregion
export { BaseSparkError as A, methodToSlug as C, ValidationError$1 as D, castJson as E, autoCastError as M, registerErrorAutoCast as N, isExpectedError as O, toCheck as S, apiKeyToToken as T, HAS_ANY as _, wildcardDomain as a, getActionsForDomain as b, err as c, InternalRoles as d, isDraft as f, HAS_ALL as g, isRetracted as h, parentDomain as i, isSparkError as j, markExpected as k, ok as l, isPublished as m, slugify as n, validate as o, isPendingRetraction as p, slugifyLoose as r, parse as s, telemetryServiceName as t, normalizeForSearch as u, RESOURCES as v, slugToMethod as w, getResourcesForDomain as x, WILDCARD as y };